import { useState, useEffect, useCallback } from 'preact/hooks';
import { authFetch } from '../api/authFetch';
import { vaNextUrl } from '../utils/estateLinks';

// ─── Cascade cost types ───────────────────────────────────────────────────────

interface TierStats { count: number; cost: number; }
interface DayStats {
  date: string;
  haiku: TierStats;
  sonnet: TierStats;
  opus: TierStats;
  signpost: TierStats;
}
interface CascadeStats { days: DayStats[]; totals: DayStats; }

const TIER_COLORS: Record<string, string> = {
  haiku:    '#64b5f6',
  sonnet:   '#81c784',
  opus:     '#ce93d8',
  signpost: '#ffb74d',
};
const TIER_LABEL: Record<string, string> = {
  haiku: 'Haiku', sonnet: 'Sonnet', opus: 'Opus', signpost: 'Signpost',
};

function fmt$(n: number): string {
  if (n < 0.001) return '<$0.001';
  return `$${n.toFixed(3)}`;
}
function fmtDate(d: string): string {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (d === today) return 'Today';
  if (d === yesterday) return 'Yest';
  return d.slice(5); // MM-DD
}

function DistributionBar({ day }: { day: DayStats }) {
  const tiers = ['sonnet', 'haiku', 'opus'] as const;
  const total = tiers.reduce((s, t) => s + day[t].count, 0);
  if (total === 0) return <div class="cascade-bar-empty">no data</div>;
  return (
    <div class="cascade-dist-bar" title={`Total: ${total} prompts`}>
      {tiers.map(t => {
        const pct = Math.round((day[t].count / total) * 100);
        if (pct === 0) return null;
        return (
          <div
            key={t}
            class="cascade-dist-segment"
            style={{ width: `${pct}%`, background: TIER_COLORS[t] }}
            title={`${TIER_LABEL[t]}: ${day[t].count} (${pct}%)`}
          />
        );
      })}
    </div>
  );
}

function CascadeCostPanel() {
  const [stats, setStats] = useState<CascadeStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch('/api/cascade/stats')
      .then(r => r.ok ? r.json() : null)
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div class="cascade-panel cascade-loading">Loading cost data…</div>;
  if (!stats) return <div class="cascade-panel cascade-error">Cascade stats unavailable</div>;

  const today = stats.days[stats.days.length - 1];
  const totalCost = ['haiku', 'sonnet', 'opus', 'signpost'].reduce(
    (s, t) => { const tier = (stats.totals as any)[t]; return s + (tier?.cost || 0); }, 0
  );

  // Recalculate properly
  const totalCostVal = stats.totals.haiku.cost + stats.totals.sonnet.cost + stats.totals.opus.cost + stats.totals.signpost.cost;
  const totalPrompts = stats.totals.haiku.count + stats.totals.sonnet.count + stats.totals.opus.count;

  return (
    <div class="cascade-panel">
      <div class="cascade-panel-header">
        <span class="cascade-title">Cascade Cost</span>
        <span class="cascade-subtitle">7-day · {fmt$(totalCostVal)} total · {totalPrompts} prompts</span>
      </div>

      {/* Today's distribution bar */}
      <div class="cascade-section-label">Today's routing</div>
      <DistributionBar day={today} />
      <div class="cascade-legend">
        {(['sonnet', 'haiku', 'opus'] as const).map(t => (
          <span key={t} class="cascade-legend-item">
            <span class="cascade-legend-dot" style={{ background: TIER_COLORS[t] }} />
            {TIER_LABEL[t]} {today[t].count > 0 ? `×${today[t].count}` : ''}
          </span>
        ))}
      </div>

      {/* 7-day cost table */}
      <div class="cascade-section-label" style={{ marginTop: '12px' }}>7-day cost by model</div>
      <div class="cascade-cost-table">
        <div class="cascade-table-row cascade-table-head">
          <span>Date</span>
          <span style={{ color: TIER_COLORS.sonnet }}>Sonnet</span>
          <span style={{ color: TIER_COLORS.haiku }}>Haiku</span>
          <span style={{ color: TIER_COLORS.opus }}>Opus</span>
          <span style={{ color: TIER_COLORS.signpost }}>Signs.</span>
          <span>Total</span>
        </div>
        {stats.days.map(d => {
          const rowTotal = d.haiku.cost + d.sonnet.cost + d.opus.cost + d.signpost.cost;
          if (rowTotal === 0) return null;
          return (
            <div key={d.date} class="cascade-table-row">
              <span class="cascade-date">{fmtDate(d.date)}</span>
              <span>{fmt$(d.sonnet.cost)}</span>
              <span>{fmt$(d.haiku.cost)}</span>
              <span>{fmt$(d.opus.cost)}</span>
              <span>{fmt$(d.signpost.cost)}</span>
              <span class="cascade-row-total">{fmt$(rowTotal)}</span>
            </div>
          );
        })}
        <div class="cascade-table-row cascade-table-foot">
          <span>Total</span>
          <span>{fmt$(stats.totals.sonnet.cost)}</span>
          <span>{fmt$(stats.totals.haiku.cost)}</span>
          <span>{fmt$(stats.totals.opus.cost)}</span>
          <span>{fmt$(stats.totals.signpost.cost)}</span>
          <span class="cascade-row-total">{fmt$(totalCostVal)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Opus escalation stats ────────────────────────────────────────────────────

interface OpusStats {
  suggestions: { sent: number; accepted: number; dismissed: number };
  escalations: { classifier: number; manual: number; self: number; total: number };
  avgTurns: number;
  truncated: number;
}

function OpusStatsPanel() {
  const [stats, setStats] = useState<OpusStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch('/api/cascade/opus-stats')
      .then(r => r.ok ? r.json() : null)
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div class="cascade-panel cascade-loading">Loading Opus stats…</div>;
  if (!stats) return null;

  const { suggestions: sug, escalations: esc } = stats;
  const acceptRate = sug.sent > 0 ? Math.round((sug.accepted / sug.sent) * 100) : 0;
  const truncRate = esc.total > 0 ? Math.round((stats.truncated / esc.total) * 100) : 0;

  // Don't show panel if no activity today
  if (esc.total === 0 && sug.sent === 0) return null;

  return (
    <div class="cascade-panel opus-stats-panel">
      <div class="cascade-panel-header">
        <span class="cascade-title" style={{ color: TIER_COLORS.opus }}>Opus Consultations</span>
        <span class="cascade-subtitle">Today</span>
      </div>

      <div class="opus-stats-grid">
        <div class="opus-stat-cell">
          <span class="opus-stat-value">{esc.total}</span>
          <span class="opus-stat-label">Total</span>
        </div>
        <div class="opus-stat-cell">
          <span class="opus-stat-value">{stats.avgTurns}</span>
          <span class="opus-stat-label">Avg turns</span>
        </div>
        <div class="opus-stat-cell">
          <span class="opus-stat-value">{truncRate}%</span>
          <span class="opus-stat-label">Truncated</span>
        </div>
        <div class="opus-stat-cell">
          <span class="opus-stat-value">{acceptRate}%</span>
          <span class="opus-stat-label">Accept rate</span>
        </div>
      </div>

      {/* Source breakdown */}
      <div class="opus-stats-breakdown">
        <div class="cascade-section-label">Escalation sources</div>
        <div class="opus-source-row">
          <span class="opus-source-label">Manual (brain btn)</span>
          <span class="opus-source-count">{esc.manual}</span>
        </div>
        <div class="opus-source-row">
          <span class="opus-source-label">Classifier suggested</span>
          <span class="opus-source-count">{esc.classifier}</span>
        </div>
        <div class="opus-source-row">
          <span class="opus-source-label">Sonnet self-escalation</span>
          <span class="opus-source-count">{esc.self}</span>
        </div>
      </div>

      {/* Suggestion funnel */}
      {sug.sent > 0 && (
        <div class="opus-stats-breakdown">
          <div class="cascade-section-label">Suggestion funnel</div>
          <div class="opus-source-row">
            <span class="opus-source-label">Suggested</span>
            <span class="opus-source-count">{sug.sent}</span>
          </div>
          <div class="opus-source-row">
            <span class="opus-source-label">Accepted</span>
            <span class="opus-source-count">{sug.accepted}</span>
          </div>
          <div class="opus-source-row">
            <span class="opus-source-label">Dismissed</span>
            <span class="opus-source-count">{sug.dismissed}</span>
          </div>
        </div>
      )}
    </div>
  );
}

interface ContainerInfo {
  name: string;
  service: string;
  state: string;
  status: string;
  health: string | null;
}

interface RebuildInfo {
  status: string;
  rebuildId?: string;
  requestedBy?: string;
  completedAt?: string;
  systemd: string;
}

interface ProjectHealth {
  project: string;
  healthy: boolean;
  live: boolean;
  containers: ContainerInfo[];
  running: number;
  total: number;
  missing: string[];
  rebuild: RebuildInfo;
}

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function chipStateClass(state: string): string {
  if (state === 'running') return 'state-running';
  if (state === 'exited' || state === 'stopped') return 'state-down';
  if (state === 'starting' || state === 'restarting') return 'state-transient';
  return 'state-unknown';
}

function rebuildStatusClass(status: string): string {
  if (status === 'success') return 'success';
  if (status === 'failed') return 'failed';
  if (status === 'building' || status === 'requested') return 'pending';
  return 'unknown';
}

function ProjectCard({ name, health }: { name: string; health: ProjectHealth }) {
  const rbCls = rebuildStatusClass(health.rebuild.status);
  const sysBadgeDanger = health.rebuild.systemd !== 'idle' && health.rebuild.systemd !== 'in_progress';

  return (
    <div class="monitor-project-card">
      <div class="monitor-project-header">
        <span class={`monitor-status-dot ${health.healthy ? 'ok' : 'bad'}`} />
        <span class="monitor-project-name">{name}</span>
        <span class="monitor-project-summary">
          {health.running}/{health.total} up{health.live ? ' · live' : ' · unreachable'}
        </span>
      </div>

      <div class="monitor-containers-grid">
        {health.containers.map((c) => (
          <div key={c.service} class="monitor-container-chip">
            <span class={`monitor-chip-dot ${chipStateClass(c.state)}`} />
            <span class="monitor-chip-name">{c.service}</span>
            <span class="monitor-chip-state">{c.state}</span>
          </div>
        ))}
        {health.missing.map((s) => (
          <div key={s} class="monitor-container-chip monitor-chip-missing">
            <span class="monitor-chip-dot state-down" />
            <span class="monitor-chip-name">{s}</span>
            <span class="monitor-chip-state">missing</span>
          </div>
        ))}
      </div>

      <div class="monitor-rebuild-row">
        <span class="monitor-rebuild-label">Last rebuild:</span>
        <span class={`monitor-rebuild-status ${rbCls}`}>{health.rebuild.status}</span>
        {health.rebuild.completedAt && (
          <span class="monitor-rebuild-meta">{timeAgo(health.rebuild.completedAt)}</span>
        )}
        {health.rebuild.requestedBy && (
          <span class="monitor-rebuild-meta">by {health.rebuild.requestedBy}</span>
        )}
        {health.rebuild.systemd !== 'idle' && (
          <span class={`monitor-systemd-badge${sysBadgeDanger ? ' danger' : ''}`}>
            systemd: {health.rebuild.systemd}
          </span>
        )}
      </div>
    </div>
  );
}

export function MonitorPage() {
  const [health, setHealth] = useState<Record<string, ProjectHealth> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await authFetch('/api/monitor/health');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setHealth(data);
      setLastRefresh(new Date());
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 15000);
    return () => clearInterval(id);
  }, [fetchHealth]);

  const projects = health ? Object.entries(health) : [];
  const allHealthy = projects.length > 0 && projects.every(([, h]) => h.healthy);

  const healthDotClass = loading
    ? 'loading'
    : projects.length === 0
      ? 'neutral'
      : allHealthy
        ? 'ok'
        : 'warn';

  return (
    <div class="memos-page monitor-page">
      <div class="memos-header monitor-header">
        <div class="memos-header-left">
          <a class="memos-back" href="/" title="Back">←</a>
          <div class="memos-header-title">
            <div class="pulse-mark-wrap">
              <div class="pulse-mark" title="Pulse — live stack health">
                <span class="pulse-c">P</span>
                <span class="pulse-c">u</span>
                <span class="pulse-c">l</span>
                <span class="pulse-c">s</span>
                <span class="pulse-c">e</span>
              </div>
              <span
                class={`monitor-health-dot ${healthDotClass}`}
                title={loading ? 'Loading…' : projects.length === 0 ? 'No projects' : allHealthy ? 'All healthy' : 'Issues detected'}
              />
            </div>
            <p class="pulse-tagline">LIVE STACK HEALTH</p>
          </div>
        </div>
        {/* The estate deep-link triangle (issue #2208): this estate-ops surface re-renders
            estate state (per-project stack health); the vessel's direction is to LINK OUT to
            va-next planes. The href is the convention's builder (utils/estateLinks.ts) — never
            a hand-rolled literal; no ?seat=/?lane= param is fabricated (monitor rows are
            projects, not dispatch seats/lanes). The re-render itself stays (links-only slice). */}
        <div class="monitor-header-actions">
          <a
            class="monitor-estate-link"
            href={vaNextUrl()}
            data-link-kind="va-next-estate"
            target="_blank"
            rel="noreferrer"
            title="Open the estate in va-next — mission control (lanes, seats, council) in a new tab"
          >
            Estate ↗
          </a>
          <button type="button" class="memos-stats monitor-refresh-btn" onClick={fetchHealth} title="Refresh now">
            {lastRefresh
              ? `${lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })} ↻`
              : '↻'}
          </button>
        </div>
      </div>

      <div class="monitor-scroll">
        {loading && <div class="monitor-message">Loading service health…</div>}
        {error && <div class="monitor-error">ops-panel unreachable — {error}</div>}
        {!loading && !error && projects.length === 0 && (
          <div class="monitor-message">No projects tracked by ops-panel.</div>
        )}
        <div class="monitor-card-stack">
          {projects.map(([name, h]) => (
            <ProjectCard key={name} name={name} health={h} />
          ))}
        </div>
        {!loading && (
          <div class="monitor-hint">ops-panel · auto-refreshes every 15s</div>
        )}
        <CascadeCostPanel />
        <OpusStatsPanel />
      </div>
    </div>
  );
}
